import { useEffect } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

/**
 * FORCE-UPDATE + NUKE-ON-UPDATE PWA controller.
 *
 * Policy (2026-09-23): every push = every client updates. No prompt, no
 * grace period. Root cause that fixed (SCHEMA.md §18, 2026-09-23 "browser
 * was still running a pre-remediation bundle"): the user's open tab served
 * the OLD bundle from the service-worker precache for hours while the
 * server had the fix.
 *
 * Policy (2026-10-02): every update also wipes ALL local site data and
 * force-unregisters every service-worker registration. Root cause that
 * motivated this (SCHEMA.md §18, 2026-10-02 prescription-print saga): even
 * after the server had the fix and the browser hard-reloaded, stale state
 * survived — old precaches, old registrations, and stale localStorage keys
 * written by old bundles. Now, whenever a NEW service worker takes control
 * of the page (`controllerchange`), the page:
 *
 *   1. deletes every Cache Storage cache (all precache generations),
 *   2. deletes every IndexedDB database on the origin,
 *   3. unregisters EVERY service-worker registration (the new one included
 *      — it re-registers fresh on the reload and rebuilds its precache
 *      from the network; orphaned legacy registrations are also killed),
 *   4. clears localStorage + sessionStorage (stale client state from old
 *      bundles — UI prefs, print prefs, legacy keys like cookieFallback /
 *      emr:sessionSecret from the 2026-09-23 incident class),
 *   5. reloads from the network.
 *
 * Deliberately NOT cleared: cookies. The auth session lives in an httpOnly
 * cookie (`a_session_…`), so users stay signed in across updates — the nuke
 * would otherwise log everyone out on every deploy. All wiped localStorage
 * keys are non-critical (verified 2026-10-02: tab/accordion prefs,
 * rx-print preference, idle-activity timestamps; the nav-pin migration is
 * idempotent because pins are server-side in `nav_pins`).
 *
 * LOOP PROTECTION (critical): after the nuke-reload, the fresh page has no
 * controller; it registers the SW, which activates (clientsClaim) and fires
 * `controllerchange` on that fresh page. Without guards that claim would
 * trigger another nuke → reload → claim → … infinite loop. Three cases in
 * `onControllerChange`:
 *
 *   - Case 1: sessionStorage round-trip flag set right before our nuke-reload
 *     (sessionStorage is per-tab and SURVIVES location.reload). Its presence
 *     proves this page load was caused by our own nuke → just clear the
 *     flag and let the claim happen.
 *   - Case 2: fresh first-ever install claiming an uncontrolled page —
 *     nothing to clean (no old state exists) and the page already runs the
 *     current bundle from the network → no-op.
 *   - Case 3: a NEW service worker took over a page that was running an
 *     older bundle → nuke + reload.
 *
 * Policy (2026-10-03): staleness reduced from ≤1h to ≤5min — the poll
 * interval dropped to 5 minutes, and every tab-focus / visibility-change
 * fires an immediate `registration.update()` (a user returning to the tab
 * picks up a just-deployed build within ~1-2s, regardless of the poll).
 *
 * Chain end-to-end: push → Cloudflare build → deploy → browser SW check
 * (5-min poll; immediate on tab focus/visibility change or fresh
 * navigation) detects new sw.js → new SW installs + activates instantly
 * (skipWaiting) → controllerchange → NUKE all local site data + unregister
 * SWs → reload from network → SW re-registers and re-precaches.
 * Worst-case staleness in an open tab: 5 minutes; ~1-2s if the user is
 * away and returns; 0 on next fresh load.
 *
 * Driven by `registerType: "autoUpdate"`. The `needRefresh` branch is kept
 * as a belt-and-braces path for the autoUpdate install flow.
 */
const SW_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/** Per-tab flag planted just before our nuke-reload; sessionStorage survives
 * location.reload() within the same tab, so the fresh page can recognize
 * its first controllerchange as our own re-claim and not nuke again. */
const NUKE_RELOAD_FLAG = "emr:pwaNukeReload";

/** Captured at module scope — BEFORE the SW registration can claim this
 * page. True = this document loaded under an existing controller, so any
 * controllerchange we see is a real takeover (update). False = this page
 * loaded uncontrolled; its first controllerchange is just the fresh SW
 * claiming it (first-ever visit, or our own post-nuke reload). */
const hadControllerAtLoad =
  typeof navigator !== "undefined" && !!navigator.serviceWorker?.controller;

/**
 * Wipe ALL local site data except cookies. Each step is independent and
 * individually non-fatal — a browser that blocks one storage type (private
 * mode, etc.) must not block the rest.
 */
async function nukeSiteData(): Promise<void> {
  // 1. Cache Storage — every cache: all precache generations (workbox
  //    versioned precaches), runtime caches, anything any SW ever created.
  if (typeof caches !== "undefined") {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }

  // 2. IndexedDB — the SPA itself uses none (verified 2026-10-02), but old
  //    bundles or future code may leave databases behind. `databases()` is
  //    Chromium-only; absent elsewhere this is simply a no-op.
  const idb = indexedDB as (IDBFactory & {
    databases?: () => Promise<{ name?: string }[]>;
  }) | undefined;
  if (idb?.databases) {
    const dbs = await idb.databases();
    await Promise.all(dbs.flatMap((d) => (d.name ? [idb.deleteDatabase(d.name)] : [])));
  }

  // 3. Unregister EVERY service-worker registration on this origin. The
  //    just-activated new SW goes too — the reload re-registers it via the
  //    normal PWA registration path and its install re-precaches from the
  //    network. This also removes any orphaned registrations from legacy
  //    scopes/paths that no current code controls.
  if (navigator.serviceWorker) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }

  // 4. localStorage — stale client state written by old bundles (UI prefs,
  //    legacy credential keys from pre-2026-09-24 bundles). All confirmed
  //    non-critical; the auth session lives in an httpOnly cookie.
  try {
    localStorage.clear();
  } catch {
    // storage may be unavailable (private mode) — non-fatal
  }

  // 5. sessionStorage — clear, then plant the round-trip flag AFTER clearing
  //    so it is the only key left. Must happen before the reload.
  try {
    sessionStorage.clear();
    sessionStorage.setItem(NUKE_RELOAD_FLAG, "1");
  } catch {
    // non-fatal — Case-2 guard in onControllerChange still breaks any loop
  }
}

export function PwaUpdater() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    // Update checks (2026-10-03): three triggers all calling
    // registration.update() — (a) a 5-minute poll for tabs that sit open
    // and never navigate (the 2026-09-23 incident scenario), (b) window
    // focus, (c) visibilitychange→visible (user returning to the tab —
    // the most common real-world update moment; a deployed build is then
    // picked up within ~1-2s). Explicit update() calls are NOT subject to
    // the browser's 24h navigation-check throttle, but each one fetches
    // /sw.js from the origin (max-age=0), so rapid focus flapping is
    // throttled here to at most one check per 15s. (vite-plugin-pwa
    // 0.20.5's RegisterSWOptions has no `interval` option — all three
    // triggers are implemented manually via onRegisteredSW.)
    onRegisteredSW(_swScriptUrl, registration) {
      if (!registration) return;
      let lastCheckAt = 0;
      const check = () => {
        const now = Date.now();
        if (now - lastCheckAt < 15_000) return;
        lastCheckAt = now;
        registration.update().catch((e: unknown) => {
          console.warn("[PWA] update check failed:", e instanceof Error ? e.message : e);
        });
      };
      setInterval(check, SW_POLL_INTERVAL_MS);
      window.addEventListener("focus", check);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    },
    onRegisterError(error) {
      // SW registration failed (e.g. browser in private mode). Non-fatal —
      // the app still works as a normal SPA, it just won't auto-update.
      console.error("[PWA] service worker registration failed", error);
    },
  });

  useEffect(() => {
    // Re-entrancy guard: nuke+reload exactly once per page lifetime.
    let nuking = false;
    // Set when this page has already been claimed once while uncontrolled
    // (fresh install or post-nuke reload) — distinguishes the first claim
    // from a later REAL takeover within the same page lifetime.
    let claimedOnce = false;

    // Safety: if the page loaded CONTROLLED (an SW served it), any nuke
    // flag left in sessionStorage by a previous page lifetime's nuke is
    // stale — consume it now. Otherwise it would suppress a future real
    // takeover's nuke in THIS page lifetime, leaving stale caches. (The
    // normal Case-1 path consumes it on the claim after our reload.)
    if (hadControllerAtLoad) {
      try {
        sessionStorage.removeItem(NUKE_RELOAD_FLAG);
      } catch {
        // non-fatal — Case 3 runs anyway on a real takeover
      }
    }

    const nukeAndReload = () => {
      if (nuking) return;
      nuking = true;
      console.info(
        "[PWA] new service worker active — clearing site data, unregistering service workers, reloading",
      );
      // Never trap the tab: if any cleanup step hangs, still reload after 3s.
      const failsafe = window.setTimeout(() => window.location.reload(), 3000);
      nukeSiteData()
        .catch((e) => {
          console.warn("[PWA] site-data cleanup failed (reloading anyway):", e);
        })
        .finally(() => {
          window.clearTimeout(failsafe);
          window.location.reload();
        });
    };

    const onControllerChange = () => {
      // Case 1: this page load was caused by our own nuke-reload (the
      // sessionStorage flag survived the reload). Consume the flag, let
      // the fresh claim stand — the precache was just rebuilt from network.
      let ourOwnReload = false;
      try {
        ourOwnReload = sessionStorage.getItem(NUKE_RELOAD_FLAG) === "1";
        if (ourOwnReload) sessionStorage.removeItem(NUKE_RELOAD_FLAG);
      } catch {
        // sessionStorage unavailable — fall through to the other guards
      }
      if (ourOwnReload) {
        claimedOnce = true;
        return;
      }

      // Case 2: fresh install claiming a page that loaded uncontrolled
      // (first-ever visit, or the claim after Case 1 already consumed the
      // flag in a storage-less browser). Nothing to clean and the page
      // already runs the current bundle from the network — nuking here
      // would loop forever.
      if (!hadControllerAtLoad && !claimedOnce) {
        claimedOnce = true;
        return;
      }

      // Case 3: a NEW service worker took over a page that was running an
      // older bundle — nuke all local state, unregister everything, and
      // reload onto the new build.
      nukeAndReload();
    };

    // MISSING LINK #2: when a NEW service worker takes over this client
    // (skipWaiting), the page is still running the OLD bundle in memory.
    // Reload immediately so the tab executes the new code. Without this,
    // activation alone changes nothing for the open tab.
    navigator.serviceWorker?.addEventListener("controllerchange", onControllerChange);
    return () => {
      navigator.serviceWorker?.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  useEffect(() => {
    if (needRefresh) {
      // autoUpdate flow: the new SW finished installing — confirm it (posts
      // SKIP_WAITING). Activation then fires controllerchange, which runs
      // the nuke+reload path above. No user prompt: the policy is
      // force-update.
      void updateServiceWorker(true);
    }
  }, [needRefresh, updateServiceWorker]);

  return null;
}
